import {
  Search,
  CheckBox,
  CheckBoxOutlineBlank,
  ExpandMore,
  ExpandLess,
  Settings,
  Extension,
} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  FormHelperText,
  InputAdornment,
  Link,
  ListSubheader,
  Paper,
  Radio,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { hostname } from "../utils";
import {
  INTEGRATION_LOGOS,
  INTEGRATION_NAMES,
} from "../dashboard/integrations/constants";

export interface Integration {
  id: string;
  name: string;
  instanceName?: string;
  type: string;
  parameters?: {
    email?: string;
    logo?: string;
    composioSlug?: string;
    composioAppSlug?: string;
    [key: string]: any;
  };
  provider?: string;
}

/**
 * Load all integrations for a company by type, handling pagination automatically.
 */
export async function loadAllIntegrations(
  token: string,
  companyId: string,
  types?: string[]
): Promise<Integration[]> {
  const allIntegrations: Integration[] = [];
  let page = 1;
  const pageSize = 1000;
  let hasMore = true;

  const params: any = { companyId, page, pageSize };
  if (types && types.length > 0) {
    params.types = types.join(",");
  }

  while (hasMore) {
    const res = await axios.get(`${hostname}/integrations`, {
      params: { ...params, page },
      headers: { Authorization: `Bearer ${token}` },
    });

    const items = res.data.items || res.data.integrations || [];
    allIntegrations.push(...items);

    // Check if there are more pages
    const total = res.data.total || items.length;
    hasMore = allIntegrations.length < total && items.length === pageSize;
    page++;

    // Safety limit to prevent infinite loops
    if (page > 100) break;
  }

  return allIntegrations;
}

// Multi-select props
interface MultiSelectProps {
  multiple: true;
  selectedIds: string[];
  onChange: (selectedIds: string[]) => void;
  selectedId?: never;
  onChangeSingle?: never;
}

// Single-select props
interface SingleSelectProps {
  multiple?: false;
  selectedId: string;
  onChangeSingle: (selectedId: string, integration: Integration | null) => void;
  selectedIds?: never;
  onChange?: never;
}

interface BaseProps {
  integrations: Integration[];
  label?: string;
  helperText?: string | React.ReactNode;
  disabled?: boolean;
  error?: boolean;
  fullWidth?: boolean;
  loading?: boolean;
  allowNone?: boolean;
  onManageIntegrations?: () => void;
}

type SearchableIntegrationSelectProps = BaseProps &
  (MultiSelectProps | SingleSelectProps);

// Get integration logo
const getIntegrationLogo = (integration: Integration): string | undefined => {
  // First check native integration logos
  if (INTEGRATION_LOGOS[integration.type]) {
    return INTEGRATION_LOGOS[integration.type];
  }

  // Check stored logo in parameters
  if (integration.parameters?.logo) {
    return integration.parameters.logo;
  }

  // For Composio integrations without a stored logo, generate the URL
  if (integration.provider === "composio") {
    const slug =
      integration.parameters?.composioSlug ||
      integration.parameters?.composioAppSlug ||
      integration.type;
    if (slug) {
      const normalizedSlug = slug.toLowerCase().replace(/_tool$/, "");
      return `https://logos.composio.dev/api/${normalizedSlug}`;
    }
  }

  return undefined;
};

// Get display name for integration type
const getIntegrationTypeName = (type: string): string => {
  return INTEGRATION_NAMES[type] || type.charAt(0).toUpperCase() + type.slice(1);
};

// Get display name for integration
const getIntegrationDisplayName = (integration: Integration): string => {
  return (
    integration.instanceName ||
    integration.name ||
    getIntegrationTypeName(integration.type)
  );
};

const SearchableIntegrationSelect: React.FC<SearchableIntegrationSelectProps> = (
  props
) => {
  const {
    integrations,
    label = "Integration",
    helperText,
    disabled = false,
    error = false,
    fullWidth = true,
    loading = false,
    allowNone = true,
    onManageIntegrations,
  } = props;

  // Normalize to work with arrays internally
  const isMultiple = props.multiple === true;
  const selectedIds = isMultiple
    ? props.selectedIds
    : props.selectedId
    ? [props.selectedId]
    : [];
  const handleChange = (newIds: string[]) => {
    if (isMultiple) {
      props.onChange(newIds);
    } else {
      const selectedIntegration =
        newIds.length > 0
          ? integrations.find((i) => i.id === newIds[0]) || null
          : null;
      props.onChangeSingle(
        newIds.length > 0 ? newIds[0] : "",
        selectedIntegration
      );
    }
  };

  const [inputValue, setInputValue] = useState("");

  // Group integrations by type
  const groupedIntegrations = useMemo(() => {
    const groups: Record<string, Integration[]> = {};

    integrations.forEach((integration) => {
      const type = integration.type || "Other";
      if (!groups[type]) {
        groups[type] = [];
      }
      groups[type].push(integration);
    });

    // Sort types alphabetically, then sort integrations within each type
    return Object.entries(groups)
      .sort(([a], [b]) => {
        const nameA = getIntegrationTypeName(a);
        const nameB = getIntegrationTypeName(b);
        return nameA.localeCompare(nameB);
      })
      .map(([type, typeIntegrations]) => ({
        type,
        typeName: getIntegrationTypeName(type),
        integrations: typeIntegrations.sort((a, b) =>
          getIntegrationDisplayName(a).localeCompare(getIntegrationDisplayName(b))
        ),
      }));
  }, [integrations]);

  // Initialize collapsed types with all types collapsed by default
  const [collapsedTypes, setCollapsedTypes] = useState<Set<string>>(
    () => new Set(groupedIntegrations.map((g) => g.type))
  );

  // Update collapsed types when new types appear
  useEffect(() => {
    const currentTypes = new Set(groupedIntegrations.map((g) => g.type));
    setCollapsedTypes((prev) => {
      const newTypes = [...currentTypes].filter((t) => !prev.has(t));
      if (newTypes.length > 0) {
        return new Set([...prev, ...newTypes]);
      }
      return prev;
    });
  }, [groupedIntegrations]);

  // Flatten integrations with type info for autocomplete
  const flattenedOptions = useMemo(() => {
    const options: Array<Integration & { groupType: string; typeName: string }> =
      [];
    groupedIntegrations.forEach(({ type, typeName, integrations: typeIntegrations }) => {
      typeIntegrations.forEach((integration) => {
        options.push({ ...integration, groupType: type, typeName });
      });
    });
    return options;
  }, [groupedIntegrations]);

  const selectedIntegrations = integrations.filter((i) =>
    selectedIds.includes(i.id)
  );

  const toggleTypeCollapse = (type: string) => {
    setCollapsedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  const selectAllInType = (type: string) => {
    // Only available in multi-select mode
    if (!isMultiple) return;

    const typeIntegrationIds = integrations
      .filter((i) => i.type === type)
      .map((i) => i.id);

    const allSelected = typeIntegrationIds.every((id) =>
      selectedIds.includes(id)
    );

    if (allSelected) {
      // Deselect all in type
      handleChange(selectedIds.filter((id) => !typeIntegrationIds.includes(id)));
    } else {
      // Select all in type
      const newIds = [...new Set([...selectedIds, ...typeIntegrationIds])];
      handleChange(newIds);
    }
  };

  const handleToggle = (integration: Integration) => {
    const isSelected = selectedIds.includes(integration.id);
    if (isMultiple) {
      if (isSelected) {
        handleChange(selectedIds.filter((id) => id !== integration.id));
      } else {
        handleChange([...selectedIds, integration.id]);
      }
    } else {
      // Single select - just set the value (or clear if clicking selected)
      handleChange(isSelected ? [] : [integration.id]);
    }
  };

  // For single select, get the single selected integration
  const singleSelectedIntegration =
    !isMultiple && selectedIds.length > 0
      ? flattenedOptions.find((i) => i.id === selectedIds[0]) || null
      : null;

  return (
    <FormControl fullWidth={fullWidth} error={error}>
      <Autocomplete
        multiple={isMultiple}
        disableCloseOnSelect={isMultiple}
        disabled={disabled || loading}
        loading={loading}
        options={flattenedOptions}
        value={isMultiple ? selectedIntegrations : singleSelectedIntegration}
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
        onChange={(_, newValue) => {
          if (isMultiple) {
            handleChange((newValue as Integration[]).map((i) => i.id));
          } else {
            handleChange(newValue ? [(newValue as Integration).id] : []);
          }
        }}
        getOptionLabel={(option) =>
          `${getIntegrationDisplayName(option)} (${getIntegrationTypeName(option.type)})`
        }
        isOptionEqualToValue={(option, value) => option.id === value.id}
        groupBy={(option) =>
          (option as Integration & { typeName: string }).typeName
        }
        filterOptions={(options, { inputValue }) => {
          const searchLower = inputValue.toLowerCase();
          return options.filter(
            (option) =>
              getIntegrationDisplayName(option)
                .toLowerCase()
                .includes(searchLower) ||
              option.type.toLowerCase().includes(searchLower) ||
              (option.parameters?.email || "")
                .toLowerCase()
                .includes(searchLower)
          );
        }}
        PaperComponent={({ children, ...props }) => (
          <Paper {...props} sx={{ maxHeight: 400 }}>
            {children}
          </Paper>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={
              selectedIds.length === 0
                ? loading
                  ? ""
                  : "Search integrations..."
                : ""
            }
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  {params.InputProps.startAdornment}
                  {selectedIds.length === 0 && !loading && (
                    <InputAdornment position="start">
                      <Search fontSize="small" color="action" />
                    </InputAdornment>
                  )}
                  {loading && (
                    <InputAdornment position="start">
                      <Typography variant="body2" color="text.secondary">
                        Loading...
                      </Typography>
                    </InputAdornment>
                  )}
                </>
              ),
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        {...(isMultiple && {
          renderTags: (value: Integration[]) => (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {value.slice(0, 3).map((integration) => (
                <Chip
                  key={integration.id}
                  label={getIntegrationDisplayName(integration)}
                  size="small"
                  avatar={
                    getIntegrationLogo(integration) ? (
                      <Box
                        component="img"
                        src={getIntegrationLogo(integration)}
                        alt=""
                        sx={{ width: 20, height: 20, borderRadius: "50%" }}
                      />
                    ) : undefined
                  }
                  onDelete={() =>
                    handleChange(selectedIds.filter((id) => id !== integration.id))
                  }
                />
              ))}
              {value.length > 3 && (
                <Chip
                  label={`+${value.length - 3} more`}
                  size="small"
                  color="primary"
                />
              )}
            </Box>
          ),
        })}
        renderGroup={(params) => {
          const typeName = params.group;
          // Find the type code from the type name
          const typeCode =
            groupedIntegrations.find((g) => g.typeName === typeName)?.type || "";
          const typeIntegrationIds = integrations
            .filter((i) => i.type === typeCode)
            .map((i) => i.id);
          const selectedInType = typeIntegrationIds.filter((id) =>
            selectedIds.includes(id)
          ).length;
          const allSelected =
            typeIntegrationIds.length > 0 &&
            selectedInType === typeIntegrationIds.length;
          const someSelected = selectedInType > 0 && !allSelected;
          const isCollapsed = collapsedTypes.has(typeCode);

          // Get logo for this type
          const typeLogo = INTEGRATION_LOGOS[typeCode];

          return (
            <Box key={params.key}>
              <ListSubheader
                sx={(theme) => ({
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? theme.palette.grey[800]
                      : theme.palette.grey[200],
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? theme.palette.grey[700]
                        : theme.palette.grey[300],
                  },
                  py: 0.5,
                })}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleTypeCollapse(typeCode);
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {isMultiple && (
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={someSelected}
                      onClick={(e) => {
                        e.stopPropagation();
                        selectAllInType(typeCode);
                      }}
                      icon={<CheckBoxOutlineBlank fontSize="small" />}
                      checkedIcon={<CheckBox fontSize="small" />}
                    />
                  )}
                  {typeLogo ? (
                    <Box
                      component="img"
                      src={typeLogo}
                      alt=""
                      sx={{ width: 18, height: 18 }}
                    />
                  ) : (
                    <Extension sx={{ fontSize: 18, color: "text.secondary" }} />
                  )}
                  <Typography variant="subtitle2" fontWeight="bold">
                    {typeName}
                  </Typography>
                  {isMultiple && (
                    <Chip
                      label={`${selectedInType}/${typeIntegrationIds.length}`}
                      size="small"
                      color={allSelected ? "primary" : "default"}
                      sx={{ height: 20, fontSize: "0.7rem" }}
                    />
                  )}
                  {!isMultiple && (
                    <Chip
                      label={typeIntegrationIds.length}
                      size="small"
                      sx={{ height: 20, fontSize: "0.7rem" }}
                    />
                  )}
                </Box>
                {isCollapsed ? (
                  <ExpandMore fontSize="small" />
                ) : (
                  <ExpandLess fontSize="small" />
                )}
              </ListSubheader>
              <Collapse in={!isCollapsed}>{params.children}</Collapse>
            </Box>
          );
        }}
        renderOption={(props, option, { selected }) => {
          const { key, ...otherProps } = props;
          const logo = getIntegrationLogo(option);

          return (
            <Box
              component="li"
              key={key}
              {...otherProps}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                py: 0.5,
                px: 1,
              }}
              onClick={(e) => {
                e.preventDefault();
                handleToggle(option);
              }}
            >
              {isMultiple ? (
                <Checkbox
                  checked={selected}
                  size="small"
                  icon={<CheckBoxOutlineBlank fontSize="small" />}
                  checkedIcon={<CheckBox fontSize="small" />}
                />
              ) : (
                <Radio checked={selected} size="small" />
              )}
              {logo ? (
                <Box
                  component="img"
                  src={logo}
                  alt=""
                  sx={{ width: 24, height: 24, borderRadius: 1 }}
                />
              ) : (
                <Extension sx={{ fontSize: 24, color: "text.secondary" }} />
              )}
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap>
                  {getIntegrationDisplayName(option)}
                </Typography>
                {option.parameters?.email && (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {option.parameters.email}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        }}
      />
      {(helperText || onManageIntegrations) && (
        <FormHelperText component="div">
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {helperText && <span>{helperText}</span>}
            {onManageIntegrations && (
              <Link
                component="button"
                type="button"
                variant="caption"
                onClick={(e) => {
                  e.preventDefault();
                  onManageIntegrations();
                }}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                <Settings sx={{ fontSize: 14 }} />
                Manage integrations
              </Link>
            )}
          </Box>
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default SearchableIntegrationSelect;
